import { Add, Check, KeyboardArrowDown, LocationOn } from "@mui/icons-material";
import { Box, Divider, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  useGetOne,
  useInfiniteGetList,
  usePermissions,
  useRedirect,
  useRefresh,
  useStore,
  useTranslate,
} from "react-admin";

import { ALL_STANDORT_VALUE, STORE_KEYS, type StoredStandort } from "../constants";
import { LOCATION_OPTIONS_PAGE_SIZE } from "../constants/pagination";
import { ResourceName } from "../constants/resources";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { color, theme, typography } from "../theme/designTokens";
import LocationCreate from "./LocationCreate";
import { buildPhysicalLocationTree, type LocationRecord as PhysicalLocationRecord } from "./LocationHierarchyFields";

const CREATE_LOCATION_VALUE = "__create-location__";

export type StandortChoice = {
  id: string;
  name: string;
  depth: number;
};

export const flattenPhysicalLocationTree = (tree: ReturnType<typeof buildPhysicalLocationTree>): StandortChoice[] => {
  const flatten = (locations: typeof tree.roots, depth: number): StandortChoice[] =>
    locations.flatMap(location => [
      { id: location.id, name: location.name, depth },
      ...flatten(tree.childrenMap.get(location.id) || [], depth + 1),
    ]);

  return flatten(tree.roots, 0);
};

type StandortSelectorProps = {
  onLocationSelected?: () => void;
};

export const StandortSelector = ({ onLocationSelected }: StandortSelectorProps) => {
  const redirect = useRedirect();
  const refresh = useRefresh();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { organizationId } = useOrganizationContext();
  const { permissions } = usePermissions();

  const [storedStandort, setStoredStandort] = useStore<StoredStandort>(
    STORE_KEYS.SELECTED_STANDORT_ID,
    ALL_STANDORT_VALUE
  );
  const selectedStandortId = typeof storedStandort === "object" ? storedStandort.id : storedStandort;
  const isLegacyStoredId = selectedStandortId !== ALL_STANDORT_VALUE && typeof storedStandort === "string";
  const {
    data: locationPages,
    error: locationOptionsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetched: isLocationsFetched,
    isPending: isLocationsPending,
  } = useInfiniteGetList<PhysicalLocationRecord>(
    "Location",
    {
      filter: { organizationId },
      pagination: { page: 1, perPage: LOCATION_OPTIONS_PAGE_SIZE },
      meta: { locationOptions: true },
    },
    { enabled: open && !!organizationId }
  );
  const {
    data: legacyLocation,
    error: legacyLocationError,
    isFetched: isLegacyLocationFetched,
    isPending: isLegacyLocationPending,
  } = useGetOne<PhysicalLocationRecord>(
    "Location",
    { id: selectedStandortId, meta: { locationOption: true, organizationId } },
    { enabled: isLegacyStoredId && !!organizationId }
  );

  const locations = useMemo(() => locationPages?.pages.flatMap(page => page.data) || [], [locationPages]);

  useEffect(() => {
    if (open && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, open]);

  const locationTree = useMemo(() => buildPhysicalLocationTree(locations || []), [locations]);
  const locationChoices = useMemo(() => flattenPhysicalLocationTree(locationTree), [locationTree]);
  const selectedLabel =
    typeof storedStandort === "object" ? storedStandort.name : legacyLocation?.name || selectedStandortId;
  const standortChoices = useMemo(() => {
    const pendingChoice =
      selectedStandortId !== ALL_STANDORT_VALUE && !locationChoices.some(choice => choice.id === selectedStandortId)
        ? { id: selectedStandortId, name: selectedLabel, depth: 0 }
        : undefined;

    return [
      { id: ALL_STANDORT_VALUE, name: translate("standortAll"), depth: 0 },
      ...(pendingChoice ? [pendingChoice] : []),
      ...locationChoices,
    ];
  }, [locationChoices, selectedLabel, selectedStandortId, translate]);
  const selectedLocationId = selectedStandortId;

  useEffect(() => {
    if (typeof storedStandort === "object" && organizationId && storedStandort.organizationId !== organizationId) {
      setStoredStandort(ALL_STANDORT_VALUE);
    }
  }, [organizationId, setStoredStandort, storedStandort]);

  useEffect(() => {
    if (!legacyLocation || !isLegacyStoredId) return;
    setStoredStandort({
      id: legacyLocation.id,
      name: legacyLocation.name,
      organizationId: organizationId || legacyLocation.organizationId || "",
    });
  }, [isLegacyStoredId, legacyLocation, organizationId, setStoredStandort]);

  useEffect(() => {
    if (
      isLegacyStoredId &&
      isLegacyLocationFetched &&
      !isLegacyLocationPending &&
      !legacyLocation &&
      !legacyLocationError
    ) {
      setStoredStandort(ALL_STANDORT_VALUE);
    }
  }, [
    isLegacyLocationFetched,
    isLegacyLocationPending,
    isLegacyStoredId,
    legacyLocation,
    legacyLocationError,
    setStoredStandort,
  ]);

  useEffect(() => {
    if (
      selectedStandortId !== ALL_STANDORT_VALUE &&
      !isLegacyStoredId &&
      !hasNextPage &&
      !isFetchingNextPage &&
      isLocationsFetched &&
      !isLocationsPending &&
      !locationOptionsError &&
      !locationChoices.some(location => location.id === selectedStandortId)
    ) {
      setStoredStandort(ALL_STANDORT_VALUE);
    }
  }, [
    hasNextPage,
    isFetchingNextPage,
    isLegacyStoredId,
    isLocationsFetched,
    isLocationsPending,
    locationChoices,
    locationOptionsError,
    selectedStandortId,
    setStoredStandort,
  ]);

  const handleStandortChange = (event: SelectChangeEvent<string>) => {
    const locationId = event.target.value;
    if (locationId === CREATE_LOCATION_VALUE) {
      setOpen(false);
      setIsCreateDialogOpen(true);
      return;
    }

    const location = locationChoices.find(choice => choice.id === locationId);
    setStoredStandort(
      location && organizationId ? { id: location.id, name: location.name, organizationId } : ALL_STANDORT_VALUE
    );
    refresh();
    redirect("/");
    onLocationSelected?.();
  };

  const renderSelectedLocation = (locationId: string) =>
    standortChoices.find(choice => choice.id === locationId)?.name || selectedLabel || translate("standortAll");

  const hasManagerAccess = permissions?.includes(ResourceName.Location) ?? false;

  return (
    <Box
      sx={{
        mr: 0,
        display: "flex",
        alignItems: "center",
        gap: "11px",
        padding: "9px 11px",
        border: `1px solid ${color.panelBorder}`,
        borderRadius: "13px",
        backgroundColor: "#F7F8FC",
        cursor: "pointer",
        minWidth: 0,
        "&:hover": {
          backgroundColor: "#F0F2F9",
        },
      }}
    >
      <LocationOn sx={{ color: color.primary, fontSize: 21, flexShrink: 0 }} />
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0 }}>
        <FormControl
          variant="standard"
          sx={{
            minWidth: 0,
            width: "100%",
            "& .MuiInput-root:before": { borderBottom: "none" },
            "& .MuiInput-root:after": { borderBottom: "none" },
            "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "none" },
          }}
        >
          <Select
            value={selectedLocationId}
            renderValue={renderSelectedLocation}
            inputProps={{ "aria-label": translate("standort") }}
            onChange={handleStandortChange}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            IconComponent={KeyboardArrowDown}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: "6px",
                  border: `1px solid ${color.panelBorder}`,
                  borderRadius: "14px",
                  boxShadow: "0 12px 30px rgba(20, 20, 50, 0.12)",
                  "& .MuiList-root": { padding: "6px" },
                },
              },
            }}
            sx={{
              minWidth: 0,
              width: "100%",
              "& .MuiSelect-select": {
                minHeight: "unset",
                padding: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "14.5px",
                fontWeight: typography.fontWeight.semibold,
                lineHeight: 1.25,
              },
              "& .MuiSelect-icon": {
                color: color.secondary40,
                right: 0,
                fontSize: 21,
              },
              color: theme.color.typographyBase,
            }}
          >
            {standortChoices.map(choice => (
              <MenuItem
                key={choice.id}
                value={choice.id}
                data-testid={`standort-option-${choice.id}`}
                data-depth={choice.depth}
                sx={{
                  minHeight: "36px",
                  padding: `8px 11px 8px ${11 + choice.depth * 22}px`,
                  borderRadius: "10px",
                  gap: "10px",
                  fontSize: "14px",
                  fontWeight: typography.fontWeight.semibold,
                  color: color.typographyBase,
                  "&:hover": { backgroundColor: "#F2F4FB" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                  <Box sx={{ width: 16, height: 20, display: "flex", flexShrink: 0 }}>
                    {choice.id === selectedLocationId && <Check fontSize="small" sx={{ color: color.primary }} />}
                  </Box>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {choice.name}
                  </Box>
                </Box>
              </MenuItem>
            ))}
            {hasManagerAccess
              ? [
                  <Divider key="create-divider" component="li" sx={{ margin: "6px 4px" }} />,
                  <MenuItem
                    key={CREATE_LOCATION_VALUE}
                    value={CREATE_LOCATION_VALUE}
                    data-testid="standort-create-option"
                    disabled={!organizationId}
                    sx={{
                      minHeight: "38px",
                      padding: "9px 11px",
                      borderRadius: "10px",
                      gap: "10px",
                      color: color.primary,
                      fontSize: "14px",
                      fontWeight: typography.fontWeight.semibold,
                      "&:hover": { backgroundColor: "#F2F4FB" },
                    }}
                  >
                    <Add sx={{ fontSize: 20, color: color.primary, flexShrink: 0 }} />
                    {translate("standortCreate")}
                  </MenuItem>,
                ]
              : null}
          </Select>
        </FormControl>
        <Typography
          variant="body2"
          sx={{
            fontSize: "10.5px",
            fontWeight: typography.fontWeight.semibold,
            letterSpacing: "0.06em",
            lineHeight: 1.25,
            textTransform: "uppercase",
            color: theme.color.secondary40,
          }}
        >
          {translate("standort")}
        </Typography>
      </Box>
      {isCreateDialogOpen && <LocationCreate open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} />}
    </Box>
  );
};
